/** @format */

import React, { useState, useEffect, useCallback } from "react";
import "./index.scss";
import debounce from "lodash/debounce";
import { useDispatch } from "react-redux";
import { CreateWaiter, UpdateWaiter, DeleteWaiter } from "store/action/WaiterAction";

const BACKEND_URL = process.env.REACT_APP_DEV_URL;

const CHECK_USERNAME = (username) =>
  new Promise((resolve) => {
    fetch(`${BACKEND_URL}/merchant/waiter/check_waiter_username_availability/${username}`).then(
      (res) => resolve(res.status === 200)
    );
  });

/* tiny inline SVGs so no extra deps */
const EditSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#4361EE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const DeleteSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#D90429" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/>
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6M14 11v6"/>
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/>
  </svg>
);
const SaveSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#2FBF71" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);
const CancelSVG = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#FF445C" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
  </svg>
);

const WaiterCardMobile = ({ initialEditing, waiterData, isNew, onCancel, onDelete }) => {
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(initialEditing || false);
  const [details, setDetails] = useState({
    username: waiterData?.username || "",
    password: waiterData?.password || "",
    waiter_info: waiterData?.waiter_info || "",
  });
  const [usernameAvailability, setUsernameAvailability] = useState(null);

  useEffect(() => {
    setDetails({
      username: waiterData?.username || "",
      password: waiterData?.password || "",
      waiter_info: waiterData?.waiter_info || "",
    });
  }, [waiterData]);

  const debouncedCheck = useCallback(
    debounce((val) => {
      CHECK_USERNAME(val).then(setUsernameAvailability);
    }, 500),
    []
  );

  const set = (field, val) => {
    setDetails((p) => ({ ...p, [field]: val }));
    if (field === "username") {
      setUsernameAvailability(null);
      debouncedCheck(val);
    }
  };

  const handleSave = () => {
    if (isNew) {
      dispatch(CreateWaiter(details));
    } else {
      dispatch(UpdateWaiter({ waiterId: waiterData.waiter_id, updatedData: details }));
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    if (isNew) {
      onCancel();
    } else {
      setIsEditing(false);
      setDetails({
        username: waiterData?.username || "",
        password: waiterData?.password || "",
        waiter_info: waiterData?.waiter_info || "",
      });
    }
  };

  const handleDelete = () => {
    if (waiterData?.waiter_id) {
      dispatch(DeleteWaiter({ waiterId: waiterData.waiter_id })).then(() =>
        onDelete(waiterData.waiter_id)
      );
    }
  };

  const canSave = details.username && details.password && details.waiter_info && usernameAvailability;
  const initials = details.waiter_info
    ? details.waiter_info.slice(0, 2).toUpperCase()
    : "W";

  return (
    <div className="wcm-card">
      {/* ── VIEW MODE ── */}
      {!isEditing && (
        <>
          <div className="wcm-header">
            <div className="wcm-avatar">{initials}</div>
            <span className="wcm-name">{details.waiter_info || "—"}</span>
          </div>

          <div className="wcm-details">
            <div className="wcm-row">
              <span className="wcm-label">Username</span>
              <span className="wcm-value">{details.username || "—"}</span>
            </div>
            <div className="wcm-row">
              <span className="wcm-label">Password</span>
              <span className="wcm-value">{"•".repeat(Math.min(details.password?.length || 0, 10))}</span>
            </div>
          </div>

          <div className="wcm-actions">
            <button className="wcm-btn-icon wcm-btn-icon--edit" onClick={() => setIsEditing(true)} title="Edit">
              <EditSVG />
            </button>
            <button className="wcm-btn-icon wcm-btn-icon--delete" onClick={handleDelete} title="Delete">
              <DeleteSVG />
            </button>
          </div>
        </>
      )}

      {/* ── EDIT / CREATE FORM ── */}
      {isEditing && (
        <div className="wcm-form">
          <div className="wcm-field">
            <label className="wcm-field-label">Name / ID</label>
            <input
              className="wcm-input"
              type="text"
              placeholder="Enter name or ID"
              value={details.waiter_info}
              onChange={(e) => set("waiter_info", e.target.value)}
            />
          </div>

          <div className="wcm-field">
            <label className="wcm-field-label">Username</label>
            <input
              className={`wcm-input${
                usernameAvailability === true
                  ? " wcm-input--success"
                  : usernameAvailability === false
                  ? " wcm-input--error"
                  : ""
              }`}
              type="text"
              placeholder="Enter username"
              value={details.username}
              onChange={(e) => set("username", e.target.value)}
            />
            {usernameAvailability !== null && (
              <span className={`wcm-availability ${usernameAvailability ? "wcm-availability--ok" : "wcm-availability--err"}`}>
                {usernameAvailability ? "✓ Username available" : "✗ Username already taken"}
              </span>
            )}
          </div>

          <div className="wcm-field">
            <label className="wcm-field-label">Password</label>
            <input
              className="wcm-input"
              type="password"
              placeholder="Enter password"
              value={details.password}
              onChange={(e) => set("password", e.target.value)}
            />
          </div>

          <div className="wcm-form-actions">
            <button className="wcm-btn wcm-btn--cancel" onClick={handleCancel}>
              <CancelSVG /> Cancel
            </button>
            <button className="wcm-btn wcm-btn--save" onClick={handleSave} disabled={!canSave}>
              <SaveSVG /> Save
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WaiterCardMobile;
